import { vec3, vec4, mat4, mat3, vec2 } from "gl-matrix";

import { MathConst, Vec3Basics, Vec4Colors } from "wFundament/wContants.js";
import { wQuad } from "../wFundament/wQuad.js";
import { wCircle } from "../wFundament/wCircle.js";
import { wSphere } from "../wFundament/wSphere.js";
import { wBox } from "../wFundament/wBox.js";
import { wLine } from "../wFundament/wLine.js";
import { wEllipsoid } from "../wFundament/wEllipsoid.js";
import { wGeoSphereBuilder } from "../wFundament/wGeoSpherebuilder.js"
import { wGizmo, wGizmoMode, wGizmoAxis } from "../wFundament/wGizmo.js";
import { wMath } from "../wFundament/wMath.js";

import { wgVertexBuffer } from "./wgVertexBuffer.js";
import { wgShader } from "./wgShader.js";
import { wgVertexDescriptor } from "./wgVertexDescriptors.js";
import { wgTexture } from "./wgTexture.js";
import { VertexType } from "./wgVertexDescriptors.js";
import { wgResource } from "./wgResource.js";

// Shaders //////////////////////////////////////////////////////
var PrimPosNormalVs = `#version 300 es
precision highp float;

layout (location = 0) in vec4 Pos;
layout (location = 1) in vec4 Color;

out vec4 col;

uniform mat4 VP;

void main() {
  col = Color;
  gl_Position = VP * vec4(Pos.xyz, 1.0);
  gl_PointSize = 2.0;
}
`;

var PrimPosNormalFs = `#version 300 es
precision highp float;

in vec4 col;
out vec4 outColor;
 
void main()
{
  outColor = col;
}
`;

var PrimTexture2DVs = `#version 300 es
precision highp float;

layout (location = 0) in vec4 Pos;
layout (location = 1) in vec4 Color;

out vec2 uv;

void main() {
  
  uv = Color.xy;
  gl_Position = Pos;
  gl_PointSize = 2.0;
}
`;

var PrimTexture2DFs = `#version 300 es
precision highp float;

in vec2 uv;
out vec4 outColor;

uniform sampler2D Texture0;
 
void main()
{
  vec3 texColor = texture(Texture0, uv).xyz; 
  outColor = abs(vec4(texColor, 1.0));
}
`;

//////////////////////////////////////////////////////////////////
var PrimVertexSize = 8;
var MaxNumberOfTextures = 8;
var NumberOfVerticesInQuad = 6;
/////////////////////////////////////////////////////////////////
class wgPrimRender extends wgResource {
    constructor(name, context) {
        super(name, context);

        this.maxNumberOfPrims_ = 1024 * 512;
        this.points_ = new Float32Array(this.maxNumberOfPrims_ * 8);
        this.lines_ = new Float32Array(this.maxNumberOfPrims_ * 8 * 2);
        this.triangles_ = new Float32Array(this.maxNumberOfPrims_ * 8 * 3);
        this.screenNdcVerts_ = new Float32Array(8 * NumberOfVerticesInQuad);

        // Texture support
        this.texture2DVerts_ = new Float32Array(8 * NumberOfVerticesInQuad * MaxNumberOfTextures);
        this.texture3DVerts_ = new Float32Array(8 * NumberOfVerticesInQuad * MaxNumberOfTextures);

        this.textures2D_ = [];
        this.textures3D_ = [];

        this.pointsUpdated_ = false;
        this.linesUpdated_ = false;
        this.trianglesUpdated_ = false;
        this.screenNdcQuadUpdated_ = false;
        this.texture2dUpdate_ = false;
        this.texture3dUpdate_ = false;

        this.numPoints_ = 0;
        this.numLines_ = 0;
        this.numTriangles_ = 0;
        this.numNdcVerts_ = 0;
        this.numTexture2d_ = 0;
        this.numTexture3d_ = 0;

        let vertexT = VertexType.VT_VertexColor4;
        this.vertexDescriptor_ = new wgVertexDescriptor(vertexT, this.getContext().gl());

        this.pointVertexBuffer_ = new wgVertexBuffer("primPointVertexBuffer", this.getContext());
        this.lineVertexBuffer_ = new wgVertexBuffer("primLineVertexBuffer", this.getContext());
        this.triangleVertexBuffer_ = new wgVertexBuffer("primTriangleVertexBuffer", this.getContext());
        this.ndcVertexBuffer_ = new wgVertexBuffer("primFullScreenVertexBuffer", this.getContext());
        this.texture2dVertexBuffer_ = new wgVertexBuffer("texture2dVertexBuffer", this.getContext());

        let geoSphereBuilder_ = new wGeoSphereBuilder();
        this.geoSphereGeometryTrianglesLod0_ = geoSphereBuilder_.buildGeoSphere(2);
        this.geoSphereGeometryTrianglesLod1_ = geoSphereBuilder_.buildGeoSphere(1);

        const gl = super.getContext().gl();

        this.pointVertexBuffer_.createVertexBuffer(
            gl.DYNAMIC_DRAW,
            this.vertexDescriptor_.vertexLayout_,
            this.maxNumberOfPrims_,
            this.vertexDescriptor_.getVertexSizeInBytes(),
            null
        );

        this.lineVertexBuffer_.createVertexBuffer(
            gl.DYNAMIC_DRAW,
            this.vertexDescriptor_.vertexLayout_,
            this.maxNumberOfPrims_ * 2,
            this.vertexDescriptor_.getVertexSizeInBytes(),
            null
        );

        this.triangleVertexBuffer_.createVertexBuffer(
            gl.DYNAMIC_DRAW,
            this.vertexDescriptor_.vertexLayout_,
            this.maxNumberOfPrims_ * 3,
            this.vertexDescriptor_.getVertexSizeInBytes(),
            null
        );

        this.ndcVertexBuffer_.createVertexBuffer(
            gl.DYNAMIC_DRAW,
            this.vertexDescriptor_.vertexLayout_,
            NumberOfVerticesInQuad,
            this.vertexDescriptor_.getVertexSizeInBytes(),
            null
        );

        this.texture2dVertexBuffer_.createVertexBuffer(
            gl.DYNAMIC_DRAW,
            this.vertexDescriptor_.vertexLayout_,
            MaxNumberOfTextures * NumberOfVerticesInQuad,
            this.vertexDescriptor_.getVertexSizeInBytes(),
            null
        );
        this.createShaders();
    }

    toNdc(v) {
        return v * 2.0 - 1.0;
    }

    createShaders() {
        this.primShader_ = new wgShader("primshader", super.getContext());
        this.primShader_.createShaderProgram(PrimPosNormalVs, PrimPosNormalFs);

        this.primTexture2dShader_ = new wgShader("PrimTextureShader", this.getContext());
        this.primTexture2dShader_.createShaderProgram(PrimTexture2DVs, PrimTexture2DFs);
    }

    addPoint(point, color) {
        if (this.numPoints_ >= this.maxNumberOfPrims_) return;

        this.points_[this.numPoints_ * 8 + 0] = point[0];
        this.points_[this.numPoints_ * 8 + 1] = point[1];
        this.points_[this.numPoints_ * 8 + 2] = point[2];
        this.points_[this.numPoints_ * 8 + 3] = point[3];

        this.points_[this.numPoints_ * 8 + 4] = color[0];
        this.points_[this.numPoints_ * 8 + 5] = color[1];
        this.points_[this.numPoints_ * 8 + 6] = color[2];
        this.points_[this.numPoints_ * 8 + 7] = color[3];

        this.numPoints_++;
        this.pointsUpdated_ = true;
    }

    addLine(p0, c0, p1, c1) {
        if (this.numLines_ >= this.maxNumberOfPrims_ * 2) return;

        this.lines_[this.numLines_ * 16 + 0] = p0[0];
        this.lines_[this.numLines_ * 16 + 1] = p0[1];
        this.lines_[this.numLines_ * 16 + 2] = p0[2];
        this.lines_[this.numLines_ * 16 + 3] = p0[3];

        this.lines_[this.numLines_ * 16 + 4] = c0[0];
        this.lines_[this.numLines_ * 16 + 5] = c0[1];
        this.lines_[this.numLines_ * 16 + 6] = c0[2];
        this.lines_[this.numLines_ * 16 + 7] = c0[3];

        this.lines_[this.numLines_ * 16 + 8] = p1[0];
        this.lines_[this.numLines_ * 16 + 9] = p1[1];
        this.lines_[this.numLines_ * 16 + 10] = p1[2];
        this.lines_[this.numLines_ * 16 + 11] = p1[3];

        this.lines_[this.numLines_ * 16 + 12] = c1[0];
        this.lines_[this.numLines_ * 16 + 13] = c1[1];
        this.lines_[this.numLines_ * 16 + 14] = c1[2];
        this.lines_[this.numLines_ * 16 + 15] = c1[3];

        this.numLines_++;
        this.linesUpdated_ = true;
    }

    addWLine(line, c0, c1) {
        if (this.numLines_ >= this.maxNumberOfPrims_ * 2) return;

        let p0 = vec4.fromValues(line.p0_[0], line.p0_[1], line.p0_[2], 1.0);
        let p1 = vec4.fromValues(line.p1_[0], line.p1_[1], line.p1_[2], 1.0);

        this.lines_[this.numLines_ * 16 + 0] = p0[0];
        this.lines_[this.numLines_ * 16 + 1] = p0[1];
        this.lines_[this.numLines_ * 16 + 2] = p0[2];
        this.lines_[this.numLines_ * 16 + 3] = p0[3];

        this.lines_[this.numLines_ * 16 + 4] = c0[0];
        this.lines_[this.numLines_ * 16 + 5] = c0[1];
        this.lines_[this.numLines_ * 16 + 6] = c0[2];
        this.lines_[this.numLines_ * 16 + 7] = c0[3];

        this.lines_[this.numLines_ * 16 + 8] = p1[0];
        this.lines_[this.numLines_ * 16 + 9] = p1[1];
        this.lines_[this.numLines_ * 16 + 10] = p1[2];
        this.lines_[this.numLines_ * 16 + 11] = p1[3];

        this.lines_[this.numLines_ * 16 + 12] = c1[0];
        this.lines_[this.numLines_ * 16 + 13] = c1[1];
        this.lines_[this.numLines_ * 16 + 14] = c1[2];
        this.lines_[this.numLines_ * 16 + 15] = c1[3];

        this.numLines_++;
        this.linesUpdated_ = true;
    }

    addEllipsoid(ellipsoid, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let ellipsoidVerts = ellipsoid.getTriangulation();
        for (let i = 0; i < ellipsoidVerts.length; i += 3) {

            this.addTriangle(
                wMath.vec4FromVec3(ellipsoidVerts[i]), color,
                wMath.vec4FromVec3(ellipsoidVerts[i + 1]), color,
                wMath.vec4FromVec3(ellipsoidVerts[i + 2]), color,
            );
        }
    }

    addWireEllipsoid(ellipsoid, color) {
        let ellipsoidVerts = ellipsoid.getTriangulation();
        for (let i = 0; i < ellipsoidVerts.length; i += 3) {
            this.addLine(wMath.vec4FromVec3(ellipsoidVerts[i]), color, wMath.vec4FromVec3(ellipsoidVerts[i + 1]), color);
            this.addLine(wMath.vec4FromVec3(ellipsoidVerts[i + 1]), color, wMath.vec4FromVec3(ellipsoidVerts[i + 2]), color);
            this.addLine(wMath.vec4FromVec3(ellipsoidVerts[i + 2]), color, wMath.vec4FromVec3(ellipsoidVerts[i]), color);
        }
    }

    addSphere(sphere, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let triangles = sphere.getTriangulation();

        for (let i = 0; i < triangles.length; i += 3) {
            let v0 = wMath.vec4FromVec3(triangles[i]);
            let v1 = wMath.vec4FromVec3(triangles[i + 1]);
            let v2 = wMath.vec4FromVec3(triangles[i + 2]);

            v0[3] = 1.0;
            v1[3] = 1.0;
            v2[3] = 1.0;
            this.addTriangle(v0, color, v1, color, v2, color);
        }
    }

    addWireGeoSphere(center, radius, color, lod) {
        if (lod == 0) {
            for (let i = 0; i < this.geoSphereGeometryTrianglesLod0_.length; i += 3) {

                const v0 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod0_[i], radius)));
                const v1 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod0_[i + 1], radius)));
                const v2 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod0_[i + 2], radius)));

                this.addLine(v0, color, v1, color);
                this.addLine(v1, color, v2, color);
                this.addLine(v2, color, v0, color);
            }
        }
        else {
            for (let i = 0; i < this.geoSphereGeometryTrianglesLod1_.length; i += 3) {

                const v0 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod1_[i], radius)));
                const v1 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod1_[i + 1], radius)));
                const v2 = wMath.vec4FromVec3(wMath.vec3Add(center, wMath.vec3Scale(this.geoSphereGeometryTrianglesLod1_[i + 2], radius)));

                this.addLine(v0, color, v1, color);
                this.addLine(v1, color, v2, color);
                this.addLine(v2, color, v0, color);
            }
        }
    }

    addWireBox(center, dimension, color) {
        const v0 = vec4.fromValues(center[0] - dimension[0] * 0.5, center[1] - dimension[1] * 0.5, center[2] - dimension[2] * 0.5, 1.0);
        const v1 = vec4.fromValues(center[0] + dimension[0] * 0.5, center[1] - dimension[1] * 0.5, center[2] - dimension[2] * 0.5, 1.0);
        const v2 = vec4.fromValues(center[0] + dimension[0] * 0.5, center[1] + dimension[1] * 0.5, center[2] - dimension[2] * 0.5, 1.0);
        const v3 = vec4.fromValues(center[0] - dimension[0] * 0.5, center[1] + dimension[1] * 0.5, center[2] - dimension[2] * 0.5, 1.0);

        const v4 = vec4.fromValues(center[0] - dimension[0] * 0.5, center[1] - dimension[1] * 0.5, center[2] + dimension[2] * 0.5, 1.0);
        const v5 = vec4.fromValues(center[0] + dimension[0] * 0.5, center[1] - dimension[1] * 0.5, center[2] + dimension[2] * 0.5, 1.0);
        const v6 = vec4.fromValues(center[0] + dimension[0] * 0.5, center[1] + dimension[1] * 0.5, center[2] + dimension[2] * 0.5, 1.0);
        const v7 = vec4.fromValues(center[0] - dimension[0] * 0.5, center[1] + dimension[1] * 0.5, center[2] + dimension[2] * 0.5, 1.0);

        // front
        this.addLine(v0, color, v1, color);
        this.addLine(v1, color, v2, color);
        this.addLine(v2, color, v3, color);
        this.addLine(v3, color, v0, color);

        // back
        this.addLine(v4, color, v5, color);
        this.addLine(v5, color, v6, color);
        this.addLine(v6, color, v7, color);
        this.addLine(v7, color, v4, color);

        //sides
        this.addLine(v0, color, v4, color);
        this.addLine(v1, color, v5, color);
        this.addLine(v2, color, v6, color);
        this.addLine(v3, color, v7, color);
    }

    addBox(box, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let triangles = box.getTriangulation();

        for (let i = 0; i < triangles.length; i += 3) {
            let v0 = wMath.vec4FromVec3(triangles[i]);
            let v1 = wMath.vec4FromVec3(triangles[i + 1]);
            let v2 = wMath.vec4FromVec3(triangles[i + 2]);

            v0[3] = 1.0;
            v1[3] = 1.0;
            v2[3] = 1.0;
            this.addTriangle(v0, color, v1, color, v2, color);
        }
    }

    addWireframeBox(box, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let triangles = box.getTriangulation();

        for (let i = 0; i < triangles.length; i += 3) {
            let v0 = wMath.vec4FromVec3(triangles[i]);
            let v1 = wMath.vec4FromVec3(triangles[i + 1]);
            let v2 = wMath.vec4FromVec3(triangles[i + 2]);
            v0[3] = 1.0;
            v1[3] = 1.0;
            v2[3] = 1.0;

            this.addLine(v0, color, v1, color);
            this.addLine(v1, color, v2, color);
            this.addLine(v2, color, v0, color);
        }
    }

    addPlaneWireframe(planeEq, dim = 8.0) {
        const planeOrigin = wMath.getPlaneOrigin(planeEq);
        const planeNormal = wMath.vec3Normalize(vec3.fromValues(planeEq[0], planeEq[1], planeEq[2]));
        let planeUp = vec3.create();
        let planeRight = vec3.create();
        vec3.cross(planeUp, Vec3Basics.X, planeNormal);
        planeUp = wMath.vec3Normalize(planeUp);
        vec3.cross(planeRight, planeUp, planeNormal);
        planeRight = wMath.vec3Normalize(planeRight);

        const normColor = vec4.fromValues(Math.abs(planeNormal[0]), Math.abs(planeNormal[1]), Math.abs(planeNormal[2]), 1.0);

        this.addLine(planeOrigin, Vec4Colors.Green, wMath.vec3Add(planeOrigin, wMath.vec3Scale(planeNormal, dim)), normColor);

        this.addLine(planeOrigin, Vec4Colors.White, wMath.vec3Add(planeOrigin, wMath.vec3Scale(planeUp, dim)), Vec4Colors.White);
        this.addLine(planeOrigin, Vec4Colors.White, wMath.vec3Add(planeOrigin, wMath.vec3Scale(planeUp, -dim)), Vec4Colors.White);

        this.addLine(planeOrigin, Vec4Colors.White, wMath.vec3Add(planeOrigin, wMath.vec3Scale(planeRight, dim)), Vec4Colors.White);
        this.addLine(planeOrigin, Vec4Colors.White, wMath.vec3Add(planeOrigin, wMath.vec3Scale(planeRight, -dim)), Vec4Colors.White);
    }

    addFrustumWireframe(frustumPoints_, color) {
        // left plane 
        this.addLine(frustumPoints_[0], color, frustumPoints_[3], color);
        this.addLine(frustumPoints_[0], color, frustumPoints_[4], color);
        this.addLine(frustumPoints_[4], color, frustumPoints_[7], color);
        this.addLine(frustumPoints_[3], color, frustumPoints_[7], color);

        // right
        this.addLine(frustumPoints_[1], color, frustumPoints_[2], color);
        this.addLine(frustumPoints_[1], color, frustumPoints_[5], color);
        this.addLine(frustumPoints_[5], color, frustumPoints_[6], color);
        this.addLine(frustumPoints_[2], color, frustumPoints_[6], color);

        // front
        this.addLine(frustumPoints_[0], color, frustumPoints_[1], color);
        this.addLine(frustumPoints_[4], color, frustumPoints_[5], color);

        // back
        this.addLine(frustumPoints_[3], color, frustumPoints_[2], color);
        this.addLine(frustumPoints_[6], color, frustumPoints_[7], color);
    }

    computeCirclePoints(circle) {
        let points = [];
        let c = circle.getCenterPoint();
        let s = circle.getNumberOfSlices();
        let angleStep = (Math.PI * 2.0) / s;
        let n = circle.getNormal();
        let r = circle.getRadius();

        let TB = wMath.frisvadONB(n);
        let matEs = wMath.gramOrthonomalization(TB[0], n, TB[1]);

        let mat = mat3.fromValues(
            matEs[0][0],
            matEs[0][1],
            matEs[0][2],
            matEs[1][0],
            matEs[1][1],
            matEs[1][2],
            matEs[2][0],
            matEs[2][1],
            matEs[2][2]
        );
        let angle = 0.0;

        for (let i = 0; i < s; i++) {
            let v0 = vec3.fromValues(Math.cos(angle) * r, 0.0, Math.sin(angle) * r);

            let vr0 = vec3.create();
            let p0 = vec3.create();

            vec3.transformMat3(vr0, v0, mat);
            vec3.add(p0, vr0, c);

            angle += angleStep;
            points[i] = p0;
        }

        return points;
    }

    addCircleWireFrameXZ(p, radius, color) {
        const s = 32;
        let angleStep = (Math.PI * 2.0) / s;
        let angle = 0.0;

        for (let i = 1; i < s + 1; i++) {
            const v0 = vec4.fromValues(Math.cos(angle) * radius, 0.0, Math.sin(angle) * radius, 0.0);
            const v1 = vec4.fromValues(Math.cos(angle + angleStep) * radius, 0.0, Math.sin(angle + angleStep) * radius, 0.0);
            this.addLine(wMath.vec4Add(v0, p), color, wMath.vec4Add(v1, p), color);
            angle += angleStep;
        }
    }

    addCircleWireFrameXY(p, radius, color) {
        const s = 32;
        let angleStep = (Math.PI * 2.0) / s;
        let angle = 0.0;

        for (let i = 1; i < s + 1; i++) {
            const v0 = vec4.fromValues(Math.cos(angle) * radius, Math.sin(angle) * radius, 0.0, 0.0);
            const v1 = vec4.fromValues(Math.cos(angle + angleStep) * radius, Math.sin(angle + angleStep) * radius, 0.0, 0.0);
            this.addLine(wMath.vec4Add(v0, p), color, wMath.vec4Add(v1, p), color);
            angle += angleStep;
        }
    }

    addCircleP(circle, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;
        let points = this.computeCirclePoints(circle);
        for (let i = 1; i < points.length; i++) {
            this.addLine(points[i - 1], color, points[i], color);
        }
        this.addLine(points[points.length - 1], color, points[0], color);
    }

    addCircleT(circle, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;
        let points = this.computeCirclePoints(circle);
        let c = circle.getCenterPoint();

        for (let i = 1; i < points.length; i++) {
            this.addTriangle(c, color, points[i - 1], color, points[i], color);
        }
        this.addTriangle(c, color, points[points.length - 1], color, points[0], color);
    }

    addCircle(circle, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let c = circle.getCenterPoint();
        let s = circle.getNumberOfSlices();
        let angleStep = (Math.PI * 2.0) / s;
        let n = circle.getNormal();
        let r = circle.getRadius();

        let TB = wMath.frisvadONB(n);
        let matEs = wMath.gramOrthonomalization(TB[0], n, TB[1]);

        let mat = mat3.fromValues(
            matEs[0][0],
            matEs[0][1],
            matEs[0][2],
            matEs[1][0],
            matEs[1][1],
            matEs[1][2],
            matEs[2][0],
            matEs[2][1],
            matEs[2][2]
        );
        let angle = 0.0;

        let v0 = vec3.fromValues(Math.cos(angle) * r, 0.0, Math.sin(angle) * r);
        angle += angleStep;

        for (let i = 1; i < s + 1; i++) {
            let v1 = vec3.fromValues(Math.cos(angle) * r, 0.0, Math.sin(angle) * r);
            let vr0 = vec3.create();
            vec3.transformMat3(vr0, v0, mat);

            let vr1 = vec3.create();
            vec3.transformMat3(vr1, v1, mat);
            let p0 = vec3.create();
            vec3.add(p0, vr0, c);
            let p1 = vec3.create();
            vec3.add(p1, vr1, c);

            this.addTriangle(c, color, p0, color, p1, color);
            angle += angleStep;
            vec3.copy(v0, v1);
        }
    }

    addCylinder(cylinder, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;
        let triangles = cylinder.getTriangulation();

        for (let i = 0; i < triangles.length; i += 3) {
            let v0 = wMath.vec4FromVec3(triangles[i]);
            let v1 = wMath.vec4FromVec3(triangles[i + 1]);
            let v2 = wMath.vec4FromVec3(triangles[i + 2]);

            v0[3] = 1.0;
            v1[3] = 1.0;
            v2[3] = 1.0;

            this.addTriangle(v0, color, v1, color, v2, color);
        }
    }

    addGizmo(gizmo) {
        if (this.numLines_ >= this.maxNumberOfPrims_ * 2) return;
        if (gizmo.getGizmoMode() == wGizmoMode.INACTIVE) return;

        let gizmoColors = [Vec4Colors.Red, Vec4Colors.Green, Vec4Colors.Blue];

        const hoverAxis = gizmo.getHoverAxis();
        const selectedAxis = gizmo.getSelectedAxis();

        if (gizmo.isDragging() && selectedAxis !== wGizmoAxis.NO_AXIS) {
            gizmoColors[selectedAxis] = Vec4Colors.Yellow;
        }
        else if (gizmo.isHovering() && hoverAxis !== wGizmoAxis.NO_AXIS) {
            gizmoColors[hoverAxis] = Vec4Colors.White;
        }

        switch (gizmo.getGizmoMode()) {
            case wGizmoMode.TRANSLATE:
                for (let i = 0; i < 3; i++) {
                    let l = gizmo.getAxisLine(i);
                    this.addLine(l.p0(), gizmoColors[i], l.p1(), gizmoColors[i]);
                    let s = new wSphere(l.p1(), l.getLength() * 0.05);
                    this.addSphere(s, gizmoColors[i]);
                }
                break;
            case wGizmoMode.ROTATE:
                for (let i = 0; i < 3; i++) {
                    const c = gizmo.getAxisCircle(i);
                    this.addCircleP(c, gizmoColors[i]);

                    if (gizmo.isDragging()) {
                        this.addPoint(gizmo.rotationUpdateStartPoint_, Vec4Colors.Grey);
                        this.addPoint(gizmo.rotationUpdatePoint_, Vec4Colors.Black);
                        this.addLine(c.getCenterPoint(),Vec4Colors.LightGrey, gizmo.rotationUpdateStartPoint_, Vec4Colors.LightGrey);      
                        this.addLine(c.getCenterPoint(), Vec4Colors.DarkGrey, gizmo.rotationUpdatePoint_, Vec4Colors.LightGrey);
                        
                       // gizmo.getAxisRotationPoints();

                        this.addTriangle(
                            c.getCenterPoint(),
                            Vec4Colors.Orange,
                            gizmo.rotationUpdateStartPoint_,
                            Vec4Colors.Orange,
                            gizmo.rotationUpdatePoint_,
                            Vec4Colors.Orange
                        );
                    }
                }
                break;
            case wGizmoMode.SCALE:
                if (gizmo.isHovering() && hoverAxis === wGizmoAxis.NO_AXIS) {
                    this.addTrianglePrimitive(gizmo.getScaleTriangle(), Vec4Colors.White);
                }
                else if(gizmo.isDragging() && gizmo.isUniformScaling()) {
                    this.addTrianglePrimitive(gizmo.getScaleTriangle(), Vec4Colors.Yellow);
                }
                else {
                    this.addTrianglePrimitive(gizmo.getScaleTriangle(), Vec4Colors.Grey);
                }

                for (let i = 0; i < 3; i++) {
                    let l = gizmo.getAxisLine(i);
                    this.addLine(l.p0(), gizmoColors[i], l.p1(), gizmoColors[i]);
                    let s = new wSphere(l.p1(), l.getLength() * 0.05, 2, 2);
                    this.addSphere(s, gizmoColors[i]);
                }

                break;
        }
    }

    addQuad(quad, color) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        let qc = quad.getQuadCorner();

        let v0 = vec4.fromValues(qc[0][0], qc[0][1], qc[0][2], 1.0);
        let v1 = vec4.fromValues(qc[1][0], qc[1][1], qc[1][2], 1.0);
        let v2 = vec4.fromValues(qc[2][0], qc[2][1], qc[2][2], 1.0);
        let v3 = vec4.fromValues(qc[3][0], qc[3][1], qc[3][2], 1.0);

        this.addTriangle(v0, color, v1, color, v2, color);
        this.addTriangle(v0, color, v2, color, v3, color);
    }

    addTrianglePrimitive(triangle, color) {
        let points = triangle.getPoints();
        this.addTriangle(points[0], color, points[1], color, points[2], color);
    }

    addTriangle(p0, c0, p1, c1, p2, c2) {
        if (this.numTriangles_ >= this.maxNumberOfPrims_ * 3) return;

        this.triangles_[this.numTriangles_ * 24 + 0] = p0[0];
        this.triangles_[this.numTriangles_ * 24 + 1] = p0[1];
        this.triangles_[this.numTriangles_ * 24 + 2] = p0[2];
        this.triangles_[this.numTriangles_ * 24 + 3] = p0[3];

        this.triangles_[this.numTriangles_ * 24 + 4] = c0[0];
        this.triangles_[this.numTriangles_ * 24 + 5] = c0[1];
        this.triangles_[this.numTriangles_ * 24 + 6] = c0[2];
        this.triangles_[this.numTriangles_ * 24 + 7] = c0[3];

        this.triangles_[this.numTriangles_ * 24 + 8] = p1[0];
        this.triangles_[this.numTriangles_ * 24 + 9] = p1[1];
        this.triangles_[this.numTriangles_ * 24 + 10] = p1[2];
        this.triangles_[this.numTriangles_ * 24 + 11] = p1[3];

        this.triangles_[this.numTriangles_ * 24 + 12] = c1[0];
        this.triangles_[this.numTriangles_ * 24 + 13] = c1[1];
        this.triangles_[this.numTriangles_ * 24 + 14] = c1[2];
        this.triangles_[this.numTriangles_ * 24 + 15] = c1[3];

        this.triangles_[this.numTriangles_ * 24 + 16] = p2[0];
        this.triangles_[this.numTriangles_ * 24 + 17] = p2[1];
        this.triangles_[this.numTriangles_ * 24 + 18] = p2[2];
        this.triangles_[this.numTriangles_ * 24 + 19] = p2[3];

        this.triangles_[this.numTriangles_ * 24 + 20] = c2[0];
        this.triangles_[this.numTriangles_ * 24 + 21] = c2[1];
        this.triangles_[this.numTriangles_ * 24 + 22] = c2[2];
        this.triangles_[this.numTriangles_ * 24 + 23] = c2[3];

        this.numTriangles_++;
        this.trianglesUpdated_ = true;
    }

    addGridXZ(dim, step) {
        let xpos = -dim * 0.5 * step;
        let lineLength = dim * step;
        const offset_ = -0.005;
        const mainOffset_ = offset_ - offset_ * 0.5;

        for (let x = 0; x <= dim; x++) {
            let p0 = vec3.fromValues(xpos, offset_, -lineLength * 0.5);
            let p1 = vec3.fromValues(xpos, offset_, lineLength * 0.5);
            let p2 = vec3.fromValues(-lineLength * 0.5, offset_, xpos);
            let p3 = vec3.fromValues(lineLength * 0.5, offset_, xpos);

            this.addLine(p0, Vec4Colors.Grey, p1, Vec4Colors.Grey);
            this.addLine(p2, Vec4Colors.Grey, p3, Vec4Colors.Grey);
            xpos += step;
        }
    }

    addOrigin(pos, scale) {
        let p0 = pos;
        let pX = vec4.fromValues(scale, 0.0, 0.0, 0.0);
        let pY = vec4.fromValues(0.0, scale, 0.0, 0.0);
        let pZ = vec4.fromValues(0.0, 0.0, scale, 0.0);

        let vX = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let vY = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let vZ = vec4.fromValues(0.0, 0.0, 0.0, 0.0);

        vec4.add(vX, p0, pX);
        vec4.add(vY, p0, pY);
        vec4.add(vZ, p0, pZ);

        this.addLine(p0, Vec4Colors.Red, vX, Vec4Colors.Red);
        this.addLine(p0, Vec4Colors.Green, vY, Vec4Colors.Green);
        this.addLine(p0, Vec4Colors.Blue, vZ, Vec4Colors.Blue);
    }

    addTexture2D(xmin, xmax, ymin, ymax, textureHandle) {
        if (this.numTexture2d_ >= MaxNumberOfTextures) return;

        let p0 = vec4.fromValues(this.toNdc(xmin), this.toNdc(ymin), 0.0, 1.0);
        let p1 = vec4.fromValues(this.toNdc(xmax), this.toNdc(ymin), 0.0, 1.0);
        let p2 = vec4.fromValues(this.toNdc(xmax), this.toNdc(ymax), 0.0, 1.0);
        let p3 = vec4.fromValues(this.toNdc(xmin), this.toNdc(ymax), 0.0, 1.0);

        let c0 = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let c1 = vec4.fromValues(1.0, 0.0, 0.0, 0.0);
        let c2 = vec4.fromValues(1.0, 1.0, 0.0, 0.0);
        let c3 = vec4.fromValues(0.0, 1.0, 0.0, 0.0);

        let offset = this.numTexture2d_ * NumberOfVerticesInQuad * 8;

        for (let i = 0; i < 4; i++) {
            this.texture2DVerts_[offset + i] = p0[i];
            this.texture2DVerts_[offset + i + 4] = c0[i];

            this.texture2DVerts_[offset + i + 8] = p2[i];
            this.texture2DVerts_[offset + i + 12] = c2[i];

            this.texture2DVerts_[offset + i + 16] = p3[i];
            this.texture2DVerts_[offset + i + 20] = c3[i];

            this.texture2DVerts_[offset + i + 24] = p0[i];
            this.texture2DVerts_[offset + i + 28] = c0[i];

            this.texture2DVerts_[offset + i + 32] = p1[i];
            this.texture2DVerts_[offset + i + 36] = c1[i];

            this.texture2DVerts_[offset + i + 40] = p2[i];
            this.texture2DVerts_[offset + i + 44] = c2[i];
        }

        this.numTexture2d_++;
        this.textures2D_.push(textureHandle);
        this.texture2dUpdate_ = true;
    }

    addTransform(transformMat4) {

        let offset = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let xAxis = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let yAxis = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let zAxis = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        vec4.transformMat4(offset, vec4.fromValues(0.0, 0.0, 0.0, 1.0), transformMat4);
        vec4.transformMat4(xAxis, vec4.fromValues(1.0, 0.0, 0.0, 0.0), transformMat4);
        vec4.transformMat4(yAxis, vec4.fromValues(0.0, 1.0, 0.0, 0.0), transformMat4);
        vec4.transformMat4(zAxis, vec4.fromValues(0.0, 0.0, 1.0, 0.0), transformMat4);

        let vX = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let vY = vec4.fromValues(0.0, 0.0, 0.0, 0.0);
        let vZ = vec4.fromValues(0.0, 0.0, 0.0, 0.0);

        vec4.add(vX, offset, xAxis);
        vec4.add(vY, offset, yAxis);
        vec4.add(vZ, offset, zAxis);

        this.addLine(offset, Vec4Colors.Red, vX, Vec4Colors.Red);
        this.addLine(offset, Vec4Colors.Green, vY, Vec4Colors.Green);
        this.addLine(offset, Vec4Colors.Blue, vZ, Vec4Colors.Blue);
    }

    clearPrimitive() {
        this.numPoints_ = 0;
        this.numLines_ = 0;
        this.numTriangles_ = 0;
        this.numNdcVerts_ = 0;

        this.linesUpdated_ = true;
        this.pointsUpdated_ = true;
        this.trianglesUpdated_ = true;
        this.screenNdcQuadUpdated_ = true;
    }

    clearTextures() {
        this.numTexture2d_ = 0;
        this.texture2dUpdate_ = true;
        while (this.textures2D_ > 0) {
            this.textures2D_.pop();
        }
    }

    update() {
        if (this.pointsUpdated_) {
            let tempBuffer = this.points_.slice(0, this.numPoints_ * PrimVertexSize);
            this.pointVertexBuffer_.updateVertexBufferData(0, tempBuffer.buffer);
            this.pointsUpdated_ = false;
        }

        if (this.linesUpdated_) {
            let tempBuffer = this.lines_.slice(0, this.numLines_ * PrimVertexSize * 2);
            this.lineVertexBuffer_.updateVertexBufferData(0, tempBuffer.buffer);
            this.linesUpdated_ = false;
        }

        if (this.trianglesUpdated_) {
            let tempBuffer = this.triangles_.slice(0, this.numTriangles_ * PrimVertexSize * 3);
            this.triangleVertexBuffer_.updateVertexBufferData(0, tempBuffer.buffer);
            this.trianglesUpdated_ = false;
        }

        if (this.texture2dUpdate_) {
            let tempBuffer = this.texture2DVerts_.slice(0, this.numTexture2d_ * PrimVertexSize * 6);
            this.texture2dVertexBuffer_.updateVertexBufferData(0, tempBuffer.buffer);
            this.texture2dUpdate_ = false;
        }
    }

    render(viewProjectionMatrix) {
        this.update();
        this.primShader_.bindProgram();
        this.primShader_.setUniformMat4fv("VP", viewProjectionMatrix);

        const gl = this.getContext().gl();

        if (this.numPoints_ > 0) {
            this.pointVertexBuffer_.bind();
            gl.drawArrays(gl.POINTS, 0, this.numPoints_);
            this.pointVertexBuffer_.unbind();
        }

        if (this.numLines_ > 0) {
            this.lineVertexBuffer_.bind();
            gl.drawArrays(gl.LINES, 0, this.numLines_ * 2);
            this.lineVertexBuffer_.unbind();
        }

        if (this.numTriangles_ > 0) {
            this.triangleVertexBuffer_.bind();
            gl.drawArrays(gl.TRIANGLES, 0, this.numTriangles_ * 3);
            this.triangleVertexBuffer_.unbind();
        }

        this.primShader_.unbindProgram();

        if (this.numTexture2d_ > 0) {
            gl.disable(gl.DEPTH_TEST);
            this.primTexture2dShader_.bindProgram();
            this.texture2dVertexBuffer_.bind();

            var offset = 0;
            for (var i = 0; i < this.numTexture2d_; i++) {
                this.textures2D_[i].bind();
                this.primTexture2dShader_.setTextureUniform("Texture0", 0);
                gl.drawArrays(gl.TRIANGLES, offset, NumberOfVerticesInQuad);
                this.textures2D_[i].unbind();
                offset += NumberOfVerticesInQuad;
            }

            this.texture2dVertexBuffer_.unbind();
            this.primTexture2dShader_.unbindProgram();

            gl.enable(gl.DEPTH_TEST);
        }
    }
}

export { wgPrimRender };
